import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  InputAdornment,
  CircularProgress,
  Box,
  Avatar,
  Tooltip,
  Stack,
  Paper,
  Grid,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@mui/material";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import useStore from "../../../helpers/useStore";
import { PatientAvailabilityDto, SalesTimeSlotDto, TimeSlotDto } from "../../../api-client";
import _ from "lodash";
import { SalesStoreContext } from "../../../stores/Sales/sales.provider";
import { AutoAwesome, CheckCircle, EditCalendar, InfoOutlined, Warning } from "@mui/icons-material";
import notificationStore from "../../../stores/Notification/notificationStore";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";
import GenericTable from "../SelectableTable";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { TherapyChip } from "../../../atoms/Chip";
import FlatCard from "../../../atoms/FlatCard";
import { useNavigate } from "react-router-dom";
import { CalendarCommands } from "../../../stores/Calendar/calendar.commands";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import AppointmentSuggestion from "../../ScheduleOverview/Appointments/AppointmentSuggestion";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import CheckOutDialog from "../CheckOutDialog";
import { methodColorKey } from "../../../helpers/slotTherapy";

const SalesTable = observer(
  ({
    patientId,
    dataRefreshFlag,
    setDataRefreshFlag,
  }: {
    patientId: number;
    dataRefreshFlag: boolean;
    setDataRefreshFlag: any;
  }) => {
    const [services, setServices] = useState<SalesTimeSlotDto[]>([]);
    const AppointmentStore = useStore(AppointmentContext);
    const { getBlockedSlotsForPatient, getPatientUnavailabilityForPatient } = AppointmentStore;
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedServices, setSelectedServices] = useState<any[]>([]); // Track selected rows
    const [checkoutSummary, setCheckoutSummary] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);
    const [isSuggestionDialogOpen, setsuggestionDialogOpen] = useState(false);
    const [blockedData, setblockedData] = useState<TimeSlotDto[]>([]);
    const [patientAvailability, setPatientAvailability] = useState<PatientAvailabilityDto[]>([]);

    const SalesStore = useStore(SalesStoreContext);
    const { getServicesByPatient } = SalesStore;
    const fetchData = async () => {
      try {
        const fetchedData = await getBlockedSlotsForPatient(patientId);
        const patientAvailabilityData = await getPatientUnavailabilityForPatient(patientId);
        setblockedData(fetchedData);
        setPatientAvailability(patientAvailabilityData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    useEffect(() => {
      // Opened by URL (`selectedSubTabStr=sales`), the tab mounts before the
      // patient id is known; the effect re-runs once it arrives.
      if (!patientId) return;
      const fetchData = async () => {
        try {
          setLoading(true);
          const data = await getServicesByPatient(patientId);
          setServices(data || []);
          if (data && data.length > 0) {
            // Filter for unpaid timeslots (no transaction.id)
            const unpaid = data.filter((item: any) => !item.transaction || !item.transaction.id);
            if (unpaid.length > 0) {
              // Select the unpaid timeslot with the highest id (most recent)
              const mostRecentUnpaid = unpaid.reduce((latest: any, item: any) =>
                item.id > latest.id ? item : latest
              );
              setSelectedServices([mostRecentUnpaid]);
            } else {
              setSelectedServices([]);
            }
          }
        } catch (error) {
          notificationStore.showMessage("Fehler beim Laden der unbezahlten Leistungen", "error");
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [patientId, dataRefreshFlag]);

    const handleDialogClose = () => {
      setOpenDialog(false);
      setCheckoutSummary([]);
      setSelectedServices([]);
    };

    const handleCheckout = () => {
      const groupedByService = _.groupBy(selectedServices, (s: any) => s.service?.id);

      const summary = Object.entries(groupedByService).flatMap(([serviceId, slots]) => {
        const service = slots[0].service;
        const abbreviation = service?.abbreviation;
        const unitPrice = Number(service?.price || 0);

        // Service row: one per service ID
        const quantity = slots.length;
        const serviceRow = {
          serviceId,
          abbreviation,
          unitPrice,
          quantity,
          total: unitPrice * quantity,
        };

        // Group slots by unique visitFee ID (or use JSON string to ensure uniqueness)
        const groupedByVisitFee = _.groupBy(slots, (slot: any) =>
          slot.visitFee
            ? JSON.stringify({ id: slot.visitFee.id, price: slot.visitFee.price })
            : "noVisitFee"
        );

        const visitFeeRows = Object.entries(groupedByVisitFee).flatMap(([key, feeSlots]) => {
          const visitFee: any = feeSlots[0].visitFee;
          if (!visitFee || !visitFee.price) return [];

          return {
            serviceId,
            abbreviation: visitFee.abbreviation || "Visit Fee",
            unitPrice: Number(visitFee.price),
            quantity: feeSlots.length,
            total: Number(visitFee.price) * feeSlots.length,
          };
        });

        return [serviceRow, ...visitFeeRows];
      });

      setCheckoutSummary(summary);
      setOpenDialog(true);
    };

    const handleRowSelection = (item: any) => {
      if (item.transaction && item.transaction.id) {
        // Show error notification or alert
        notificationStore.showMessage(
          "Diese Leistung wurde bereits bezahlt und kann nicht erneut ausgewählt werden.",
          "warning"
        ); // Or use Snackbar, toast, etc.
        return;
      }
      setSelectedServices((prevSelectedRows) => {
        const isSelected = prevSelectedRows.includes(item);
        if (isSelected) {
          // Remove the item from selected rows if it's already selected
          return prevSelectedRows.filter((row) => row !== item);
        } else {
          // Add the item to selected rows
          return [...prevSelectedRows, item];
        }
      });
    };

    const handleSelectAll = () => {
      const unpaidServices = services.filter(
        (service: any) => !service.transaction || !service.transaction.id
      );

      const hasPaidServices = services.length !== unpaidServices.length;

      if (hasPaidServices) {
        notificationStore.showMessage(
          "Einige Leistungen wurden bereits bezahlt und wurden bei der Auswahl ausgeschlossen.",
          "warning"
        );
      }

      if (selectedServices.length === unpaidServices.length) {
        setSelectedServices([]); // Deselect all if already selected
      } else {
        setSelectedServices(unpaidServices); // Select only unpaid services
      }
    };

    const totalSelected = selectedServices.length;
    const totalSelectedPrice = selectedServices.reduce((total, service) => {
      return total + Number(service.service?.price || 0);
    }, 0);

    // Conditionally add aggregates for the columns that need them
    // const aggregates = [
    //   {
    //     label: "Selected",  // Label for the first column
    //     value: totalSelected,     // Value of selected items (for example, count)
    //     columnName: "Therapie",    // Match the "Therapie" column
    //   },
    //   {
    //     label: "Price",     // Label for the second column
    //     value: `€${totalSelectedPrice.toFixed(2)}`,  // Total price of selected items
    //     columnName: "Preis (€)",      // Match the "Preis" column
    //   },

    // {
    //     label: "",
    //     value: (
    //       <Button variant="contained" onClick={handleSelectAll}>
    //         {selectedServices.length === services.length ? "Deselect All" : "Select All"}
    //       </Button>
    //     ), // Adding the button as the value
    //     columnName: "Status", // You can put the button in the last column
    //   },
    // ];
    const openSuggestionDialog = () => {
      setsuggestionDialogOpen(true);
    };
    const navigate = useNavigate();
    const calendarStore = useStore(CalendarContext);
    const { navigateToCalendar } = CalendarCommands(calendarStore);

    const handlePlanning = (patientId: number) => {
      navigateToCalendar(navigate, {
        patientId: patientId
      });
    };
    const handleAppointmentSuggestionClose = (wasSaved: boolean) => {
      setsuggestionDialogOpen(false);
      if (wasSaved) {
        setDataRefreshFlag(true);
      }
    };

    const groupedByAppointment = _.groupBy(
      services,
      (s: any) => s.appointment?.id ?? "Unbekannter Termin"
    );

    const columns = [
      {
        label: "Therapie",
        render: (item: any) => (
          <TherapyChip
            therapyName={item.service?.abbreviation || ""}
            colorKey={
              item.service?.therapyMethodId
                ? methodColorKey(item.service.therapyMethodId)
                : undefined
            }
          />
        ),
      },
      {
        label: "Addresse",
        render: (item: any) => item.appointment?.address?.type ?? "-",
      },
      {
        label: "Preis (€)",
        render: (item: any) => item.service?.price ?? "-",
      },
      {
        label: "Pauschale (€)",
        render: (item: any) =>
          item.visitFee ? `${item.visitFee.price} (${item.visitFee.abbreviation})` : "-",
      },
      {
        label: "Scheduled Status",
        render: (item: any) => {
          if (item.start && item.end) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle color="success" />
                <Typography variant="body2">
                  {formatDateUI(item.start)}
                  {formatTime(item.start)} - {formatTime(item.end)}
                </Typography>
              </Box>
            );
          }

          return (
            <Tooltip title="Unvollständig">
              <InfoOutlined />
            </Tooltip>
          );
        },
      },
      {
        label: "Mitarbeiter",
        render: (item: any) =>
          item.user ? (
            <Tooltip title={`${item.user.firstName} ${item.user.lastName}`}>
              <MobiliAvatar
                user={item?.user}
                isTherapist
                sx={{ width: 25, height: 25, fontSize: "0.8em" }}
              />
            </Tooltip>
          ) : (
            "-"
          ),
      },

      {
        label: "Status",
        render: (item: any) =>
          item.transaction ? (
            <Tooltip title={item.transaction.id}>
              <CheckCircle color="success" />
            </Tooltip>
          ) : (
            <Tooltip title="Unbezahlt">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2">Unbezahlt</Typography>
                <Warning color="warning" />
              </Box>
            </Tooltip>
          ),
      },
    ];

    return (
      <>
        {loading ? (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress />
          </Typography>
        ) : services.length === 0 ? (
          <Typography variant="body1">Keine unbezahlten Leistungen gefunden.</Typography>
        ) : (
          <>
            <>
              {/* Table */}

              <FlatCard title={undefined}>
                <GenericTable
                  columns={columns}
                  data={services}
                  isLoading={loading}
                  emptyMessage="Keine Dienste gefunden."
                  onRowClick={handleRowSelection}
                  isSelected={(item) => selectedServices.includes(item)}
                />
                <Grid container sx={{ mt: 1, px: 2 }}>
                  <Grid item xs={12} />
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Ausgewählt:</strong> {selectedServices.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" align="right">
                      €{totalSelectedPrice.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      onClick={handleSelectAll}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {selectedServices.length === services.length ? "Abwählen" : "Alle auswählen"}
                    </Button>
                  </Grid>
                </Grid>
              </FlatCard>

              {/* Totals row aligned with table columns */}
              <CheckOutDialog
                patientId={patientId}
                open={openDialog}
                onClose={handleDialogClose}
                isGiftCardCheckOut={false}
                checkoutSummary={checkoutSummary}
                selectedServices={selectedServices}
                dataRefreshFlag={dataRefreshFlag}
                setDataRefreshFlag={setDataRefreshFlag}
              />

              {/* Action buttons */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    sx={{ marginRight: 1 }}
                    onClick={openSuggestionDialog}
                    startIcon={<AutoAwesome />}
                  >
                    KI
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handlePlanning(patientId)}
                    startIcon={<EditCalendar />}
                  >
                    Planen
                  </Button>
                </Stack>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCheckout}
                  sx={{ mt: { xs: 2, sm: 0 } }}
                >
                  In den Warenkorb legen
                </Button>
              </Box>
              <AppointmentSuggestion
                open={isSuggestionDialogOpen}
                onClose={handleAppointmentSuggestionClose}
                patientId={patientId}
                blockedData={blockedData}
                setblockedData={setblockedData}
                patientAvailabilityData={patientAvailability}
                setPatientAvailabilityData={setPatientAvailability}
              />
            </>

            {/* <ServicesBillPayment
              open={paymentDialogOpen}
              handleClose={handleDialogClose}
              selectedServices={selectedServices}
              giftCertificate={{
                gcId: giftCardInfo.gcId,
                amountApplied: giftCardInfo.amountToUse,
                isGiftCertificateComp: false,
              }}
              setDataRefreshFlag={setDataRefreshFlag}
              dataRefreshFlag={dataRefreshFlag}
              totalAmount={grandTotal}
              patientId={patientId}
            /> */}
          </>
        )}
      </>
    );
  }
);

export default SalesTable;
